import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const Leaderboard = () => {
  const { userProfile } = useAuth();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [selectedCategory, setSelectedCategory] = useState('points');

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedPeriod, selectedCategory]);

  const fetchLeaderboard = async () => {
    // Mock data - replace with actual Supabase queries
    const mockData = [
      {
        id: 1,
        rank: 1,
        name: 'Alex Thompson',
        points: 8450,
        avatar: null,
        change: 'up',
        isCurrentUser: false
      },
      {
        id: 2,
        rank: 2,
        name: 'Maria Garcia',
        points: 7890,
        avatar: null,
        change: 'same',
        isCurrentUser: false
      },
      {
        id: 3,
        rank: 3,
        name: 'James Wilson',
        points: 7320,
        avatar: null,
        change: 'up',
        isCurrentUser: false
      },
      {
        id: 4,
        rank: 4,
        name: userProfile?.full_name || 'You',
        points: 6850,
        avatar: null,
        change: 'down',
        isCurrentUser: true
      },
      {
        id: 5,
        rank: 5,
        name: 'Sarah Chen',
        points: 6420,
        avatar: null,
        change: 'up',
        isCurrentUser: false
      }
    ];
    setLeaderboardData(mockData);
  };

  const periods = [
    { value: 'weekly', label: 'This Week' },
    { value: 'monthly', label: 'This Month' },
    { value: 'alltime', label: 'All Time' }
  ];

  const categories = [
    { value: 'points', label: 'Total Points' },
    { value: 'streak', label: 'Check-in Streak' },
    { value: 'workouts', label: 'Workouts Completed' },
    { value: 'classes', label: 'Classes Attended' }
  ];

  const getRankIcon = (rank) => {
    if (rank === 1) return { icon: 'Trophy', color: 'var(--color-warning)' };
    if (rank === 2) return { icon: 'Medal', color: 'var(--color-muted-foreground)' };
    if (rank === 3) return { icon: 'Award', color: 'var(--color-accent)' };
    return null;
  };

  const getChangeIcon = (change) => {
    if (change === 'up') return { icon: 'TrendingUp', color: 'var(--color-success)' };
    if (change === 'down') return { icon: 'TrendingDown', color: 'var(--color-error)' };
    return { icon: 'Minus', color: 'var(--color-muted-foreground)' };
  };

  return (
    <div className="card">
      <div className="mb-6">
        <h2 className="text-xl font-heading font-semibold text-foreground mb-1">
          Leaderboard
        </h2>
        <p className="text-sm text-muted-foreground">
          See how you rank against other members
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex gap-2 flex-wrap">
          {periods?.map((period) => (
            <button
              key={period?.value}
              onClick={() => setSelectedPeriod(period?.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedPeriod === period?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {period?.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories?.map((cat) => (
            <button
              key={cat?.value}
              onClick={() => setSelectedCategory(cat?.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === cat?.value
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {cat?.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {leaderboardData?.map((member) => {
          const rankIcon = getRankIcon(member?.rank);
          const changeIcon = getChangeIcon(member?.change);
          
          return (
            <div
              key={member?.id}
              className={`p-4 rounded-lg border transition-all duration-250 ${
                member?.isCurrentUser
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border hover:border-border/60 hover:bg-muted/30'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 flex-shrink-0">
                  {rankIcon ? (
                    <Icon name={rankIcon?.icon} size={32} color={rankIcon?.color} />
                  ) : (
                    <span className="text-2xl font-bold text-muted-foreground">
                      {member?.rank}
                    </span>
                  )}
                </div>
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-primary">
                    {member?.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-foreground truncate">
                      {member?.name}
                    </h3>
                    {member?.isCurrentUser && (
                      <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-medium">
                        You
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Icon name="Coins" size={14} color="var(--color-primary)" />
                    <span className="text-sm font-medium text-primary">
                      {member?.points?.toLocaleString()} points
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name={changeIcon?.icon} size={16} color={changeIcon?.color} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 rounded-lg bg-muted/50 border border-border">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Info" size={16} />
          <span>
            Rankings update daily. Keep earning points to climb the leaderboard!
          </span>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;